'use client'
import { useState } from 'react'
import { motion } from 'framer-motion'
import type { ManifestoPillar } from '@/lib/data/manifesto'
import { FadeInUp } from '@/components/ui/FadeInUp'
import { PILLAR_ICONS } from './PillarIcons'

type Props = {
  pillar: ManifestoPillar
  /** Where the card sits in its row. The opposite column gets the text. */
  cardSide: 'right' | 'left'
  lang: 'fa' | 'en'
  isRTL: boolean
}

// brighter gradient than lib/styles GRADIENT_TEXT_BLUE — the section bg is
// dark enough that the cooler blues there would crush; these read sharp.
const METRIC_GRADIENT = {
  backgroundImage: 'linear-gradient(90deg,#60a5fa,#22d3ee)',
  WebkitBackgroundClip: 'text' as const,
  WebkitTextFillColor: 'transparent' as const,
  backgroundClip: 'text' as const,
}

/**
 * Zigzag row — card alternates side, text fills the other column.
 *
 * Cards are pure illustration: just the SVG over a layered gradient with
 * a faint dot grid and a lit top edge. All copy lives in the paired text
 * column so the card reads as a graphic, never as a data tile.
 */
export function PillarCard({ pillar, cardSide, lang, isRTL }: Props) {
  const [active, setActive] = useState(false)
  const Icon = PILLAR_ICONS[pillar.iconKey]

  return (
    <FadeInUp>
      <div
        className={`group/row grid gap-3 md:gap-12 items-stretch ${
          cardSide === 'left' ? 'md:grid-cols-[480px_1fr]' : 'md:grid-cols-[1fr_480px]'
        }`}
        onMouseEnter={() => setActive(true)}
        onMouseLeave={() => setActive(false)}
        onFocus={() => setActive(true)}
        onBlur={() => setActive(false)}
      >
        {/* ─── Card (pure SVG stage) ─────────────────────────────────── */}
        <motion.div
          tabIndex={0}
          role="img"
          aria-label={lang === 'fa' ? pillar.fa : pillar.en}
          className={`relative aspect-[5/3] md:aspect-[2/1] rounded-[1.5rem] overflow-hidden outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 ${
            cardSide === 'left' ? 'md:order-1' : 'md:order-2'
          }`}
          style={{
            background: `
              radial-gradient(130% 90% at 50% 0%, rgba(96,165,250,0.22) 0%, transparent 60%),
              linear-gradient(170deg, #1c3055 0%, #0e1a35 100%)
            `,
            boxShadow: '0 12px 28px -16px rgba(0,0,0,0.5)',
          }}
          animate={{ y: active ? -8 : 0, scale: active ? 1.015 : 1 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* lit top edge — brightens on hover */}
          <motion.div
            aria-hidden
            className="absolute top-0 inset-x-0 h-[2px] pointer-events-none"
            style={{
              background:
                'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.7) 50%, transparent 100%)',
            }}
            animate={{ opacity: active ? 1 : 0.5 }}
            transition={{ duration: 0.4 }}
          />

          {/* diagonal shine sweep — fires once each time `active` flips true */}
          <motion.div
            aria-hidden
            className="absolute inset-y-0 w-1/3 pointer-events-none"
            style={{
              background:
                'linear-gradient(110deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)',
              filter: 'blur(8px)',
            }}
            initial={false}
            animate={active ? { x: ['-50%', '350%'] } : { x: '-100%' }}
            transition={active ? { duration: 1.1, ease: 'easeOut' } : { duration: 0 }}
          />

          {/* dot-grid pattern, masked at edges */}
          <motion.div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: 'radial-gradient(rgba(255,255,255,0.12) 1px, transparent 1px)',
              backgroundSize: '14px 14px',
              maskImage: 'radial-gradient(75% 75% at 50% 50%, black 0%, transparent 85%)',
              WebkitMaskImage: 'radial-gradient(75% 75% at 50% 50%, black 0%, transparent 85%)',
            }}
            animate={{ opacity: active ? 0.35 : 0.6 }}
            transition={{ duration: 0.4 }}
          />

          {/* corner glow on hover */}
          <motion.div
            aria-hidden
            className="absolute -top-1/2 left-1/2 -translate-x-1/2 w-3/4 h-full rounded-full blur-3xl pointer-events-none"
            style={{
              background: 'radial-gradient(circle, rgba(34,211,238,0.5), transparent 70%)',
            }}
            animate={{ opacity: active ? 1 : 0 }}
            transition={{ duration: 0.5 }}
          />

          {/* border ring + outer shadow */}
          <motion.div
            aria-hidden
            className="absolute inset-0 rounded-[1.5rem] pointer-events-none"
            initial={false}
            animate={{
              boxShadow: active
                ? 'inset 0 0 0 1px rgba(255,255,255,0.35), 0 32px 60px -18px rgba(34,211,238,0.5)'
                : 'inset 0 0 0 1px rgba(255,255,255,0.14), 0 0 0 rgba(0,0,0,0)',
            }}
            transition={{ duration: 0.4 }}
          />

          {/* SVG — center stage, fills the card */}
          <div className="absolute inset-2 md:inset-3 flex items-center justify-center">
            <Icon active={active} id={`pillar-${pillar.iconKey}`} />
          </div>
        </motion.div>

        {/* ─── Text panel ────────────────────────────────────────────── */}
        {/* No rest-state opacity dimming on the container — that was
            multiplying through the per-element colors and crushing the
            secondary copy into the navy background. Hover still feels
            different thanks to the accent x-shift + description y-shift. */}
        <div
          className={`flex flex-col justify-center gap-2 ${
            cardSide === 'left' ? 'md:order-2' : 'md:order-1'
          }`}
        >
          {/* metric — big gradient number, or title text when no accent */}
          <motion.div
            /* py/-my pair: extends the paint box so background-clip:text doesn't
               shave Pelak's ascenders/descenders, without shifting layout */
            className={`font-display font-black py-1.5 -my-1.5 ${pillar.accent ? 'text-3xl md:text-4xl lg:text-5xl leading-tight' : 'text-lg md:text-xl lg:text-2xl leading-snug'}`}
            style={METRIC_GRADIENT}
            animate={{ scale: active ? 1.04 : 1 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
          >
            {lang === 'fa' ? pillar.numFA : pillar.num}
          </motion.div>

          {/* accent + sub-heading on one tight row */}
          {pillar.accent && (
            <motion.div
              className="mt-1 flex items-baseline gap-2 flex-wrap"
              animate={{
                x: active ? 0 : (cardSide === 'left' ? -8 : 8) * (isRTL ? -1 : 1),
              }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            >
              <span className="font-display font-bold text-xl md:text-2xl text-white">
                {lang === 'fa' ? pillar.accent.fa : pillar.accent.en}
              </span>
              <span className="text-base md:text-lg font-medium text-white">
                · {lang === 'fa' ? pillar.fa : pillar.en}
              </span>
            </motion.div>
          )}

          {/* description — full white for max legibility on the navy bg */}
          <motion.p
            className="text-base md:text-lg leading-relaxed max-w-md font-medium text-white"
            animate={{ y: active ? 0 : 3 }}
            transition={{ duration: 0.4, delay: active ? 0.08 : 0 }}
          >
            {lang === 'fa' ? pillar.descFA : pillar.descEN}
          </motion.p>

          {/* connector line */}
          <motion.div
            aria-hidden
            className="h-px mt-1 rounded-full"
            style={{
              background:
                'linear-gradient(90deg, rgba(34,211,238,0.95) 0%, rgba(34,211,238,0) 100%)',
              transformOrigin: cardSide === 'right' ? 'right center' : 'left center',
            }}
            initial={false}
            animate={{ scaleX: active ? 1 : 0, opacity: active ? 1 : 0 }}
            transition={{ duration: 0.5, ease: 'easeOut', delay: active ? 0.18 : 0 }}
          />
        </div>
      </div>
    </FadeInUp>
  )
}
